import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Pagination } from '../../components/client';
import FilterBar from '../../components/shared/FilterBar';
import MarketingListingCard from '../../components/home/MarketingListingCard';
import MarketingBrowseEmpty from '../../components/home/MarketingBrowseEmpty';
import MarketingBrowseShell, {
  MarketingBrowseGrid,
  MarketingBrowseSkeleton,
} from '../../components/home/MarketingBrowseShell';
import { usePublicVendorServices } from '../../services/vendorService';
import useUserLocation from '../../hooks/useUserLocation';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const searchQuery = searchParams.get('q') || '';
  const locationParam = searchParams.get('location') || '';
  const countryParam = (searchParams.get('country') || '').trim().toUpperCase();

  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [location, setLocation] = useState(locationParam);
  const [marketCountry, setMarketCountry] = useState(countryParam);
  const [priceMin, setPriceMin] = useState(1);
  const [priceMax, setPriceMax] = useState(1000);
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedRating, setSelectedRating] = useState('all');
  const [sortBy, setSortBy] = useState('none');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [services, setServices] = useState([]);
  const [servicesPagination, setServicesPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 12,
    total: 0,
  });
  const [servicesCurrentPage, setServicesCurrentPage] = useState(1);

  const { userLocation, loading: locationLoading } = useUserLocation();

  useEffect(() => {
    if (searchQuery) setSearchTerm(searchQuery);
    if (locationParam) setLocation(locationParam);
    if (countryParam) setMarketCountry(countryParam);
  }, [searchQuery, locationParam, countryParam]);

  useEffect(() => {
    setServicesCurrentPage(1);
  }, [
    searchTerm,
    location,
    marketCountry,
    priceMin,
    priceMax,
    sortBy,
    selectedRating,
    selectedPriceRange,
  ]);

  const sortOptions = [
    { value: 'none', label: 'Recommended' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'reviews', label: 'Most Reviews' },
    { value: 'distance', label: 'Nearest' },
  ];

  const clearAllFilters = () => {
    setSearchTerm('');
    setLocation('');
    setMarketCountry('');
    setPriceMin(1);
    setPriceMax(1000);
    setSelectedPriceRange('all');
    setSelectedRating('all');
    setSortBy('none');
    setServicesCurrentPage(1);
    navigate('/search', { replace: true });
  };

  const servicesParams = useMemo(() => {
    const baseParams = {
      ...(searchTerm && { search: searchTerm }),
      ...(priceMin && priceMin > 1 && { min_price: priceMin }),
      ...(priceMax && priceMax < 1000 && { max_price: priceMax }),
      ...(selectedRating &&
        selectedRating !== 'all' && {
          min_rating: parseFloat(selectedRating.replace('+', '')),
        }),
      per_page: 12,
      page: servicesCurrentPage,
    };

    if (marketCountry) {
      baseParams.country = marketCountry;
    } else if (userLocation?.country) {
      baseParams.country = userLocation.country;
    }

    if (userLocation?.latitude && userLocation?.longitude && !marketCountry) {
      baseParams.latitude = userLocation.latitude;
      baseParams.longitude = userLocation.longitude;
      if (sortBy === 'distance' || sortBy === 'none') {
        baseParams.sort_by = 'distance';
        baseParams.sort_order = 'asc';
      }
    }

    if (sortBy !== 'none' && sortBy !== 'distance' && !baseParams.sort_by) {
      baseParams.sort_by =
        sortBy === 'rating'
          ? 'rating'
          : sortBy === 'price_low' || sortBy === 'price_high'
            ? 'price'
            : 'created_at';
      baseParams.sort_order = sortBy === 'price_low' ? 'asc' : 'desc';
    }

    return baseParams;
  }, [
    searchTerm,
    priceMin,
    priceMax,
    selectedRating,
    servicesCurrentPage,
    userLocation,
    marketCountry,
    sortBy,
  ]);

  const hasCountryFilter = !!servicesParams.country;
  const shouldFetch = hasCountryFilter && (!locationLoading || !!marketCountry);

  const { data: servicesResp, isLoading: isServicesLoading } =
    usePublicVendorServices(servicesParams, {
      enabled: shouldFetch,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      keepPreviousData: false,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    if (isServicesLoading) return;

    let servicesData = [];
    if (servicesResp?.success !== false) {
      if (servicesResp?.data?.data && Array.isArray(servicesResp.data.data)) {
        servicesData = servicesResp.data.data;
      } else if (Array.isArray(servicesResp?.data)) {
        servicesData = servicesResp.data;
      } else if (Array.isArray(servicesResp)) {
        servicesData = servicesResp;
      }
    }

    const paginationData = servicesResp?.data || {};
    setServicesPagination({
      current_page: paginationData.current_page || 1,
      last_page: paginationData.last_page || 1,
      per_page: paginationData.per_page || 12,
      total: paginationData.total || 0,
    });

    const transformed = servicesData
      .map((service) => {
        const vendorProfile = service.vendor_profile || service.vendorProfile;
        const vendorUser = vendorProfile?.user || vendorProfile?.User;
        const city = vendorProfile?.city || '';
        const state = vendorProfile?.state || '';
        const locationStr =
          city && state
            ? `${city}, ${state}`
            : city || state || 'Location not available';
        const title = service.service_name || service.serviceName || '';
        if (!service.id || !title) return null;

        return {
          id: service.id,
          title,
          rating:
            service.average_rating ||
            service.averageRating ||
            vendorProfile?.average_rating ||
            0,
          price: parseFloat(service.base_price || service.basePrice || 0),
          image:
            service.image_url ||
            service.imageUrl ||
            vendorProfile?.image_url ||
            vendorProfile?.imageUrl ||
            vendorUser?.profile_image_url ||
            vendorUser?.profileImageUrl ||
            '',
          location: locationStr,
          isFeatured: service.is_featured || service.isFeatured || false,
          vendor:
            vendorProfile?.business_name ||
            vendorProfile?.businessName ||
            'Vendor',
          reviewCount:
            service.total_reviews ||
            service.totalReviews ||
            vendorProfile?.total_reviews ||
            0,
        };
      })
      .filter(Boolean);

    setServices(transformed);
  }, [servicesResp, isServicesLoading]);

  const isLoading = isServicesLoading || locationLoading;
  const countLabel = isLoading
    ? 'Loading…'
    : `${services.length} service${services.length === 1 ? '' : 's'}`;

  const filterSlot = (
    <FilterBar
      selectedPriceRange={selectedPriceRange}
      setSelectedPriceRange={setSelectedPriceRange}
      selectedRating={selectedRating}
      setSelectedRating={setSelectedRating}
      showMoreFilters={showMoreFilters}
      setShowMoreFilters={setShowMoreFilters}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      location={location}
      setLocation={setLocation}
      selectedCategory=""
      setSelectedCategory={() => {}}
      categories={[]}
      sortBy={sortBy}
      setSortBy={setSortBy}
      sortOptions={sortOptions}
      clearAllFilters={clearAllFilters}
      showCategoryFilter={false}
      priceMin={priceMin}
      setPriceMin={setPriceMin}
      priceMax={priceMax}
      setPriceMax={setPriceMax}
    />
  );

  return (
    <MarketingBrowseShell
      mobileTitle="Search"
      title={searchTerm ? `Results for “${searchTerm}”` : 'Search results'}
      countLabel={countLabel}
      locationLabel={location?.trim() || null}
      filterSlot={filterSlot}
    >
      {isLoading ? (
        <MarketingBrowseSkeleton />
      ) : services.length > 0 ? (
        <>
          <MarketingBrowseGrid>
            {services.map((service) => (
              <MarketingListingCard
                key={service.id}
                href={`/services/${service.id}`}
                image={service.image}
                title={service.title}
                subtitle={service.vendor}
                location={service.location}
                rating={service.rating}
                price={service.price}
                priceSuffix="per event"
                reviewCount={service.reviewCount}
                featured={service.isFeatured}
              />
            ))}
          </MarketingBrowseGrid>
          {!locationLoading ? (
            <Pagination
              currentPage={servicesPagination.current_page}
              lastPage={servicesPagination.last_page}
              perPage={servicesPagination.per_page}
              total={servicesPagination.total}
              onPageChange={setServicesCurrentPage}
            />
          ) : null}
        </>
      ) : !locationLoading && !isServicesLoading ? (
        <MarketingBrowseEmpty
          title="No services found"
          description={
            searchTerm
              ? `No services found for “${searchTerm}”${
                  location ? ` in ${location}` : ''
                }.`
              : 'Try adjusting your search or filters.'
          }
          onClear={clearAllFilters}
        />
      ) : null}
    </MarketingBrowseShell>
  );
};

export default SearchResults;
